<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left">Devices</h1>
                <v-card
                    class="w-50"
                    color="surface-light"
                >
                    <v-card-text class="pa-2">
                        <v-text-field
                            v-model="quickSearch"
                            append-inner-icon="mdi-magnify"
                            autofocus
                            clearable
                            density="compact"
                            hide-details
                            label="Quick Search"
                            single-line
                            variant="solo"
                            @click:clear="fetchDevices"
                            @keydown.enter="applyQuickSearch"
                            @click:append-inner="applyQuickSearch"
                        ></v-text-field>
                    </v-card-text>
                </v-card>
                <div v-if="user.type === 'admin'">
                    <v-btn class="me-3" color="black" prepend-icon="mdi-upload" @click="importDialogSwitch = true">
                        Import
                    </v-btn>
                    <v-btn color="black" prepend-icon="mdi-plus" @click="createDevice">Add</v-btn>
                </div>
                <v-btn v-else color="black" prepend-icon="mdi-plus"
                       @click="navigateTo('setup-wizard')">Activate Device
                </v-btn>
            </v-col>
        </v-row>
        <v-row v-if="user.type !== 'admin' && (expiredDeviceCount > 0 || expiringDeviceCount > 0)">
            <v-col cols="12" class="px-5">
                <v-alert v-if="expiredDeviceCount > 0" type="error" variant="tonal" density="compact" class="mb-2" closable>
                    <strong>{{ expiredDeviceCount }} device(s) have expired subscriptions.</strong>
                    Tracking services are paused. Please renew to continue tracking.
                    <template v-slot:append>
                        <v-btn size="small" variant="outlined" color="error" @click="navigateTo('invoices')">View Invoices</v-btn>
                    </template>
                </v-alert>
                <v-alert v-if="expiringDeviceCount > 0" type="warning" variant="tonal" density="compact" closable>
                    <strong>{{ expiringDeviceCount }} device(s) expiring within 30 days.</strong>
                    Renew early to avoid service interruption.
                </v-alert>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="8">
                <v-chip-group v-if="device_counts.length">
                    <v-chip v-for="dc in device_counts" :key="dc.key"
                            :color="dc.count > 0 ? dc.color : undefined"
                            :variant="dc.count > 0 ? 'tonal' : 'outlined'"
                            :prepend-icon="dc.icon" density="comfortable" size="small"
                            class="cursor-pointer" @click="applyCountFilter(dc)">
                        {{ dc.label }}: {{ dc.count }}
                    </v-chip>
                </v-chip-group>
            </v-col>
            <v-col class="text-end pe-5 d-flex align-center justify-end">
                <v-btn-toggle v-if="user.type === 'admin'" v-model="showInactive" density="compact" variant="outlined" color="primary" class="me-2"
                              @update:model-value="currentPage = 1; fetchDevices()">
                    <v-btn :value="true" size="small">
                        <v-icon start :icon="showInactive ? 'mdi-eye' : 'mdi-eye-off'"/>{{ showInactive ? 'Hide Inactive' : 'Show Inactive' }}
                    </v-btn>
                </v-btn-toggle>
                <v-btn class="me-2" icon="mdi-refresh" size="small" variant="text"
                       @click="()=>{currentPage=1;fetchDevices()}"/>
                <v-btn class="me-2" icon="mdi-filter-variant" size="small" variant="text"
                       @click="filtersDialogSwitch = true"/>
                <v-menu v-if="Object.keys(activeFilters).length">
                    <template v-slot:activator="{ props }">
                        <v-btn icon="mdi-filter-menu" size="small" v-bind="props" variant="text"/>
                    </template>

                    <v-list class="border pa-0" density="compact">
                        <v-list-item v-for="(filter,key) in activeFilters" :key="key" class="py-0 border-b">
                            <div class="d-flex justify-space-between align-center">
                                <span>{{ filter.label }}</span>
                                <v-btn icon="mdi mdi-close" size="small" variant="plain" @click="removeFilter(key)"/>
                            </div>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th></th>
                        <th v-if="user.type === 'admin'">Portal</th>
                        <th v-if="user.type === 'admin'">Company</th>
                        <th-btn :sortedColumn="sortedColumn" column="label" sortable title="Asset" @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn" column="sku" sortable title="SKU" @sort="sort"/>
                        <th-btn title="Sl. No." column="slno" :sortedColumn="sortedColumn" sortable @sort="sort"/>
                        <th-btn title="IMEI" column="imei" :sortedColumn="sortedColumn" sortable @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn" column="bt_mac" sortable title="BT MAC" @sort="sort"/>
                        <th>Voltage</th>
                        <th>Version</th>
                        <th v-if="user.type === 'admin'">Groups</th>
                        <th-btn title="Status" column="status" :sortedColumn="sortedColumn" sortable @sort="sort"/>
                        <th v-if="user.type !== 'admin'">Subscription</th>
                        <th-btn :sortedColumn="sortedColumn" column="last_updated" sortable title="Last Reported"
                                @sort="sort"/>
                        <th>Address</th>
                        <th v-if="user.type === 'admin' || user.id === user.company?.owner_id">Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="device in devices" v-if="devices.length" :key="device.id" class="text-left">
                        <td>
                            <div class="d-flex justify-space-between">
                                <v-icon size="small" v-bind="signals(device.signal_bars??'')"/>
                                <v-icon size="small" v-bind="device.stats['battery']"/>
                                <v-tooltip location="top">
                                    <template v-slot:activator="{ props }">
                                        <v-btn density="compact" flat icon size="small" v-bind="props">
                                            <v-icon :color="device.stats['status_color']['color']">
                                                mdi-circle
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>{{ device.stats['status_color']['message'] }}</span>
                                </v-tooltip>
                            </div>
                        </td>
                        <td v-if="user.type === 'admin'">{{ getPortal(device.portal_id)?.name ?? '' }}</td>
                        <td v-if="user.type === 'admin'">{{ getCompany(device.company_id)?.company_name ?? '' }}</td>
                        <td>{{ device.label }}</td>
                        <td>{{ device.sku }}</td>
                        <td>
                            <router-link :to="`devices/${device.slno}`" target="_blank">{{
                                    device.slno
                                }}
                            </router-link>
                        </td>
                        <td>{{ device.imei }}</td>
                        <td>{{ device.bt_mac }}</td>
                        <td>{{
                                `${strOperations().formatVoltage(device.voltage1)} / ${strOperations().formatVoltage(device.voltage2)}`
                            }}
                        </td>
                        <td>{{ device.version_mod1 }}</td>
                        <td v-if="user.type === 'admin'">
                            <div>
                                <v-chip-group>
                                    <template v-for="(device_groups, index) in device.device_groups" :key="index">
                                        <v-chip size="x-small" style="max-width: 160px;">
                                            <span class="me-1 text-truncate">{{ device_groups.label }}</span>
                                            <v-icon icon="mdi-close-circle"
                                                    @click="deviceGroupDetach(device, device_groups)"/>
                                        </v-chip>
                                    </template>
                                </v-chip-group>
                                <v-btn color="grey-lighten-2" density="compact" icon="mdi-plus" size="x-small"
                                       @click="editUpdateDeviceGroup(device)"/>
                            </div>
                        </td>
                        <td class="text-capitalize">{{ status(device.status) }}</td>
                        <td v-if="user.type !== 'admin'">
                            <div class="d-flex flex-column ga-1">
                                <v-chip v-if="subscriptionStatus(device) === 'expired'" size="small" color="red" variant="tonal" prepend-icon="mdi-alert-circle">Expired</v-chip>
                                <v-chip v-else-if="subscriptionStatus(device) === 'expiring'" size="small" color="amber-darken-2" variant="tonal" prepend-icon="mdi-clock-alert">Expiring Soon</v-chip>
                                <v-chip v-else-if="subscriptionStatus(device) === 'active'" size="small" color="success" variant="tonal">Active</v-chip>
                                <span v-else class="text-grey text-caption">-</span>
                                <span v-if="device.subscription_plan_name" class="text-caption text-grey-darken-1">{{ device.subscription_plan_name }}</span>
                            </div>
                        </td>
                        <td class="text-sm-body-2">{{ formatDateTime(device.last_updated) }}</td>
                        <td class="text-sm-body-2 cursor-pointer">
                            <v-tooltip :text="getKnownLocation(device)" location="top">
                                <template v-slot:activator="{ props }">
                                    <v-chip v-bind="props" variant="text">
                                        <template v-slot:default>
                                            <v-chip density="compact" variant="text"
                                                    @click="openStreetView(device)">
                                                {{ (getKnownLocation(device).substr(0, 20) + '...') }}
                                            </v-chip>
                                        </template>
                                        <template v-slot:append>
                                            <v-btn
                                                icon="mdi-content-copy"
                                                class="d-inline ms-2"
                                                density="compact"
                                                variant="text"
                                                size="small"
                                                @click="copyAddress(device)"
                                            />
                                        </template>
                                    </v-chip>
                                </template>
                            </v-tooltip>
                        </td>
                        <td>
                            <div class="d-flex align-center ga-1">
                            <NoteIcon v-if="user.super" entity-type="devices" :entity-id="device.id" :note-count="device.notes_count ?? 0"/>
                            <v-btn-group v-if="user.type === 'admin'"
                                         density="compact" divided variant="outlined">
                                <v-btn :disabled="createDialogSwitch" class="text-info"
                                       prepend-icon="mdi-content-copy"
                                       size="x-small" @click="copyDevice(device)">
                                    Copy
                                </v-btn>
                                <v-btn :disabled="editDialogSwitch" class="text-warning" prepend-icon="mdi-pencil"
                                       size="x-small" @click="editDevice(device)">Edit
                                </v-btn>
                                <v-btn class="text-error" prepend-icon="mdi-delete" size="x-small"
                                       @click="deleteDevice(device)">Delete
                                </v-btn>
                                <v-menu>
                                    <template v-slot:activator="{ props }">
                                        <v-btn icon="mdi-dots-vertical" v-bind="props" variant="text"></v-btn>
                                    </template>

                                    <v-list density="compact">
                                        <v-list-item title="Device Configuration" @click="configFile(device)"/>
                                        <v-list-item title="Download Device Config"
                                                     @click="downloadConfigFile(device)"/>
                                        <v-list-item title="Send Message" @click="sendMessage(device)"/>
                                        <v-list-item title="Sim Status" @click="getSimStatus(device)"/>
                                        <v-list-item title="OTA Status" @click="getOtaStatus(device)"/>
                                        <v-list-item title="Show Message Logs" @click="openMessageLogs(device)"/>
                                        <v-list-item title="Payment Exclusion" @click="paymentExclusion(device)"/>
                                        <v-list-item :to="`battery-graphs/${device.id}`" target="_blank"
                                                     title="Battery Graphs"/>
                                        <v-list-item :to="`gps-graphs/${device.id}`" target="_blank"
                                                     title="GPS Graphs"/>
                                        <v-list-item :to="`reboot-graphs/${device.id}`" target="_blank"
                                                     title="Reboot History"/>
                                        <v-list-item :title="device.save_raw_packets ? 'Disable Raw Packet Logging' : 'Enable Raw Packet Logging'"
                                                     :prepend-icon="device.save_raw_packets ? 'mdi-database-off' : 'mdi-database'"
                                                     :class="device.save_raw_packets ? 'text-success' : ''"
                                                     @click="toggleSaveRawPackets(device)"/>
                                        <v-divider v-if="user.super" class="my-1"/>
                                        <v-list-item v-if="user.super"
                                                     title="Repair License"
                                                     prepend-icon="mdi-key-chain"
                                                     @click="repairLicense(device)"/>
                                        <v-divider v-if="user.super" class="my-1"/>
                                        <v-list-item v-if="user.super"
                                                     class="text-error"
                                                     prepend-icon="mdi-delete-forever"
                                                     title="Force Delete"
                                                     @click="openForceDelete(device)"/>
                                    </v-list>
                                </v-menu>
                            </v-btn-group>

                            <v-btn-group v-else-if="user.id === user.company?.owner_id">
                                <v-btn class="text-error"
                                       size="x-small"
                                       variant="elevated"
                                       @click="endSubscription(device)">
                                    End Subscription
                                </v-btn>
                            </v-btn-group>
                            </div>
                        </td>
                    </tr>
                    <tr v-else>
                        <td colspan="100%">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>

        <v-row justify="center" align="center">
            <v-col>
                <div class="d-flex align-center justify-center">
                    <v-pagination v-if="totalPages > 1"
                        class="my-4"
                        density="compact"
                        :length="totalPages"
                        v-model="currentPage"
                        @update:model-value="fetchDevices"
                    ></v-pagination>
                    <v-select v-model="perPage" :items="[10, 25, 50, 100]" density="compact"
                              variant="outlined" hide-details label="Rows" style="width: 100px"
                              class="ms-4 flex-grow-0"
                              @update:model-value="currentPage = 1; fetchDevices()"/>
                </div>
            </v-col>
        </v-row>
    </v-container>

    <filters-dialog
        v-if="filtersDialogSwitch"
        v-model="filtersDialogSwitch"
        :filters="filters"

        @applyFilters="()=>{currentPage=1;fetchDevices()}"
        @resetFilters="clearFilters"
    />

    <Import-Device v-if="user.type === 'admin' && importDialogSwitch" v-model="importDialogSwitch"
                   @updateDevices="fetchDevices"/>
    <Create-Device v-if="user.type === 'admin' && createDialogSwitch" v-model="createDialogSwitch"
                   :device="selectedDevice" @addDevice="addDevice"/>
    <Edit-Device v-if="editDialogSwitch" v-model="editDialogSwitch" :device="selectedDevice"
                 @updateDevice="updateDevice"/>

    <EditUpdateDeviceGroups v-if="deviceGroupDialogSwitch" v-model="deviceGroupDialogSwitch" :device="selectedDevice"
                            :deviceGroups="device_groups" @updateDeviceGroup="updateGroupDevices"/>

    <!-- Old ActivateDevice dialog removed - activation now uses the setup wizard -->
    <Sim-Status v-if="user.type === 'admin' && simStatusDialogSwitch" v-model="simStatusDialogSwitch"
                :device="selectedDevice"/>
    <Ota-Status v-if="user.type === 'admin' && otaStatusDialogSwitch" v-model="otaStatusDialogSwitch"
                :device="selectedDevice"/>
    <Payment-Exclusion v-if="user.type === 'admin' && paymentExclusionDialogSwitch"
                       v-model="paymentExclusionDialogSwitch"
                       :device="selectedDevice"/>

    <message-create-edit v-if="messageDialogSwitch" v-model="messageDialogSwitch" :device="selectedDevice"/>
    <config-file-create-edit v-if="configFileDialogSwitch" v-model="configFileDialogSwitch"
                             :device="selectedDevice.id"/>

    <Create-Asset v-if="createAssetDialogSwitch" v-model="createAssetDialogSwitch"
                  :pre-selected-device="preSelectedDeviceForAsset" @addAsset="onAssetCreated"/>

    <v-dialog v-model="paymentMethodRequired" max-width="450" persistent>
        <v-card>
            <v-card-title class="d-flex align-center">
                <v-icon color="primary" class="me-2">mdi-credit-card-off</v-icon>
                Payment Method Required
            </v-card-title>
            <v-card-text>
                <template v-if="isAdmin">
                    A payment method is required before you can {{ paymentMethodAction }}.
                    Please add a payment method to your company account.
                </template>
                <template v-else>
                    A payment method is required before you can {{ paymentMethodAction }}.
                    Please contact your company administrator to add a payment method.
                </template>
            </v-card-text>
            <v-card-actions>
                <v-spacer/>
                <v-btn @click="paymentMethodRequired = false">
                    {{ isAdmin ? 'Cancel' : 'OK' }}
                </v-btn>
                <v-btn v-if="isAdmin" color="primary" prepend-icon="mdi-credit-card"
                       @click="paymentMethodRequired = false; navigateTo('payment-methods')">
                    Add Payment Method
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!--
        Force delete has no undo and no archive behind it: the JSON the browser
        saves at the end is the only copy of what was removed. Two steps on
        purpose. The first shows exactly what would go, the second makes the
        operator type the serial back before the button will fire.
    -->
    <v-dialog v-model="forceDeleteDialog" max-width="760" persistent scrollable>
        <v-card v-if="forceDeleteDevice">
            <v-card-title class="d-flex align-center text-error">
                <v-icon class="me-2">mdi-delete-forever</v-icon>
                Force Delete Device
            </v-card-title>
            <v-card-subtitle class="pb-2">
                {{ forceDeleteDevice.slno }}
                <span v-if="forceDeletePreview?.device?.product_sku">
                    &middot; {{ forceDeletePreview.device.product_sku }}
                </span>
            </v-card-subtitle>

            <v-card-text style="max-height: 60vh">
                <div v-if="forceDeleteLoading" class="d-flex align-center ga-3 py-6">
                    <v-progress-circular indeterminate size="24"/>
                    Working out what would be deleted...
                </div>

                <template v-else-if="forceDeletePreview">
                    <v-alert v-if="forceDeletePreview.blockers?.length" class="mb-4" density="compact"
                             type="error" variant="tonal">
                        <div class="font-weight-medium mb-1">This device cannot be force deleted.</div>
                        <ul class="ps-4">
                            <li v-for="(blocker, i) in forceDeletePreview.blockers" :key="i">{{ blocker }}</li>
                        </ul>
                    </v-alert>

                    <template v-if="forceDeleteStep === 1">
                        <v-alert class="mb-4" density="compact" type="warning" variant="tonal">
                            This permanently removes the device and everything listed below. There is no undo
                            and no archive. Your browser will download a JSON file of every deleted row, and
                            that file contains device secrets, so store it somewhere safe.
                        </v-alert>

                        <v-alert v-if="forceDeleteInvoiceCount" class="mb-4" density="compact"
                                 type="error" variant="tonal">
                            {{ forceDeleteInvoiceCount.toLocaleString() }} invoice line
                            {{ forceDeleteInvoiceCount === 1 ? 'item' : 'items' }} will be permanently
                            deleted (recoverable only from the downloaded JSON). The invoices themselves
                            stay, with fewer lines than they were issued with.
                        </v-alert>

                        <div class="text-subtitle-2 mb-1">Assets</div>
                        <div v-if="!forceDeletePreview.assets?.length" class="text-medium-emphasis mb-4">
                            No asset is attached to this device.
                        </div>
                        <v-table v-else class="mb-4" density="compact">
                            <thead>
                            <tr>
                                <th>ID</th>
                                <th>Label</th>
                                <th>Attached via</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="asset in forceDeletePreview.assets" :key="asset.id">
                                <td>{{ asset.id }}</td>
                                <td>{{ asset.label }}<span v-if="asset.soft_deleted"> (soft deleted)</span></td>
                                <td>{{ asset.attached_via }}</td>
                            </tr>
                            </tbody>
                        </v-table>

                        <div class="text-subtitle-2 mb-1">Rows to be deleted</div>
                        <v-table class="mb-2" density="compact">
                            <thead>
                            <tr>
                                <th>Table</th>
                                <th class="text-right">Rows</th>
                                <th class="text-right">Via asset, not this device</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="row in forceDeleteRows" :key="row.table">
                                <td>{{ row.table }}</td>
                                <td class="text-right">{{ row.count.toLocaleString() }}</td>
                                <td class="text-right" :class="row.notThisDevice ? 'text-warning' : 'text-disabled'">
                                    {{ row.notThisDevice ? row.notThisDevice.toLocaleString() : '-' }}
                                </td>
                            </tr>
                            </tbody>
                            <tfoot>
                            <tr class="font-weight-bold">
                                <td>Total</td>
                                <td class="text-right">{{ (forceDeletePreview.total_rows || 0).toLocaleString() }}</td>
                                <td class="text-right">
                                    {{ forceDeleteForeignTotal ? forceDeleteForeignTotal.toLocaleString() : '-' }}
                                </td>
                            </tr>
                            </tfoot>
                        </v-table>
                        <div class="text-caption text-medium-emphasis mb-4">
                            Estimated download size {{ formatBytes(forceDeletePreview.estimated_json_bytes) }}.
                            <template v-if="forceDeleteForeignTotal">
                                {{ forceDeleteForeignTotal.toLocaleString() }} rows are reached through this
                                device's asset rather than the device itself. That is normal for an asset's own
                                history, but check the asset is not attached elsewhere.
                            </template>
                        </div>

                        <template v-if="forceDeletePreserved.length">
                            <div class="text-subtitle-2 mb-1">Kept, not deleted</div>
                            <p class="text-caption text-medium-emphasis mb-2">
                                These rows survive. Only their link to this device is cleared.
                            </p>
                            <v-table density="compact">
                                <tbody>
                                <tr v-for="row in forceDeletePreserved" :key="row.table">
                                    <td>{{ row.table }}</td>
                                    <td class="text-right">{{ row.count.toLocaleString() }}</td>
                                </tr>
                                </tbody>
                            </v-table>
                        </template>
                    </template>

                    <template v-else>
                        <v-alert class="mb-4" density="compact" type="error" variant="tonal">
                            Last chance. {{ (forceDeletePreview.total_rows || 0).toLocaleString() }} rows across
                            {{ forceDeleteRows.length }} tables will be destroyed<span v-if="forceDeleteInvoiceCount">,
                            including {{ forceDeleteInvoiceCount.toLocaleString() }} invoice line
                            {{ forceDeleteInvoiceCount === 1 ? 'item' : 'items' }}</span>.
                        </v-alert>
                        <p class="mb-3">
                            Type the serial <strong>{{ forceDeleteDevice.slno }}</strong> to confirm.
                        </p>
                        <v-text-field v-model="forceDeleteConfirm" autofocus density="compact"
                                      label="Device serial" spellcheck="false" variant="outlined"/>
                        <v-alert v-if="forceDeleteError" density="compact" type="error" variant="tonal">
                            {{ forceDeleteError }}
                        </v-alert>
                    </template>
                </template>

                <div v-else class="text-medium-emphasis py-6">
                    {{ forceDeleteError || 'Preview unavailable.' }}
                </div>
            </v-card-text>

            <v-card-actions>
                <v-btn v-if="forceDeleteStep === 2" :disabled="forceDeleteBusy" variant="text"
                       @click="forceDeleteStep = 1">
                    Back
                </v-btn>
                <v-spacer/>
                <v-btn :disabled="forceDeleteBusy" @click="forceDeleteDialog = false">Cancel</v-btn>
                <v-btn v-if="forceDeleteStep === 1" :disabled="!forceDeletePreview || forceDeleteBlocked"
                       color="error" variant="elevated" @click="forceDeleteStep = 2">
                    Continue
                </v-btn>
                <v-btn v-else :disabled="!forceDeleteSerialMatches || forceDeleteBlocked"
                       :loading="forceDeleteBusy" color="error" prepend-icon="mdi-delete-forever"
                       variant="elevated" @click="runForceDelete">
                    Delete Permanently
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {computed, onMounted, onUnmounted, reactive, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useRoute, useRouter} from "vue-router";
import ThBtn from "@/components/globals/th-btn.vue";
import {strOperations, useNavigation, isCompanyAdminRole} from "@/composables/globals";
import {formatDateTime} from '@/composables/useTimezone';
import {useClipboard} from "@/composables/clipboard";
import {useLoadingStore} from "@/stores/loadingStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import EditDevice from "@/components/dialogs/devices/Edit.vue";
import OtaStatus from "@/components/dialogs/devices/OtaStatus.vue";
import ImportDevice from "@/components/dialogs/devices/Import.vue";
import CreateDevice from "@/components/dialogs/devices/Create.vue";
import SimStatus from "@/components/dialogs/devices/SimDetails.vue";
import FiltersDialog from "@/components/cards/devices/FiltersDialog.vue";
// Old ActivateDevice dialog removed - activation now uses the setup wizard
import PaymentExclusion from "@/components/dialogs/devices/PaymentExclusion.vue";
import MessageCreateEdit from "@/components/dialogs/devices/commands/Create_Edit.vue";
import ConfigFileCreateEdit from "@/components/dialogs/devices/configs/Create_Edit.vue";
import EditUpdateDeviceGroups from "@/components/dialogs/devices/EditUpdateDeviceGroups.vue";
import CreateAsset from "@/components/dialogs/assets/Create.vue";
import {geocodeLatLng, isValidAddress} from "@/composables/useGeocode";
import {powerSourceIcon} from "@/composables/usePowerSourceIcon";
import NoteIcon from "@/components/dialogs/notes/NoteIcon.vue";

const portals = ref([]);
const devices = ref([]);
const authStore = useAuthStore();
const {user} = authStore;
const {navigateTo} = useNavigation();
const router = useRouter();
const companies = ref([]);
const totalPages = ref(1);
const currentPage = ref(1);
const perPage = ref(parseInt(localStorage.getItem('devicePerPage')) || 10);
const totalDevices = ref(0);
const locations = ref([]);
const productSkus = ref([]);
const sortedType = ref(null);
const quickSearch = ref(null);
const sortedColumn = ref(null);
const showInactive = ref(undefined);
const geocodeCache = ref({});
const device_counts = ref([]);
const device_groups = ref([]);
const selectedDevice = ref(null);
const loadingStore = useLoadingStore();
const {copyToClipboard} = useClipboard();
const {showSnackbar} = useSnackbarStore();
const editDialogSwitch = ref(false);
const createDialogSwitch = ref(false);
const importDialogSwitch = ref(false);
const assignDialogSwitch = ref(false);
const messageDialogSwitch = ref(false);
const filtersDialogSwitch = ref(false);
const simStatusDialogSwitch = ref(false);
const otaStatusDialogSwitch = ref(false);
const configFileDialogSwitch = ref(false);
const deviceGroupDialogSwitch = ref(false);
const paymentExclusionDialogSwitch = ref(false);
const deviceStatuses = ref([
    {id: 0, title: 'Active'},
    {id: 1, title: 'Disabled'},
    {id: 2, title: 'Faulty'},
    {id: 3, title: 'Lost'}
]);
const filters = reactive({
    serial_num: {label: 'Serial Number', data: null, action: null},
    label: {label: 'Asset Name', data: null},
    company: {
        label: 'Company',
        type: user.type === 'admin' ? 'autocomplete' : 'hidden',
        data: null,
        title: 'company_name',
        value: 'id',
        searchKeys: ['company_name', 'owner_name', 'email', 'account_code', 'id'],
        source: companies
    },
    sku: {label: 'Product SKU', type: 'select', data: null, title: 'sku', value: 'id', source: productSkus},
    device_status: {
        label: 'Device Status',
        type: 'select',
        data: null,
        title: 'title',
        value: 'id',
        source: deviceStatuses
    },
    imei: {label: 'IMEI', data: null, action: null},
    bt_mac: {label: 'BT MAC', type: 'text', data: null},
    lora_mac: {label: 'LORA MAC', type: 'text', data: null},
    geo_radius: {label: 'Radius', type: 'number', data: null, latitude: null, longitude: null},
    voltage1: {label: 'Device Voltage 1', type: 'number', data: null, action: null},
    voltage2: {label: 'Device Voltage 2', type: 'number', data: null, action: null},
    device_fleet_status: {label: 'Fleet Status', data: null, type: 'hidden'},
});
const activeFilters = computed(() => {
    const activatedFilters = {};
    for (const [key, filter] of Object.entries(filters)) {
        if (filter.data !== null) {
            quickSearch.value = null;
            activatedFilters[key] = filter;
        }
    }
    return activatedFilters;
});

const removeFilter = (key) => {
    filters[key]['data'] = null;
    filters[key]['action'] ? filters[key]['action'] = null : '';
    totalPages.value = 1;
    currentPage.value = 1;
    totalDevices.value = 0;
    sortedType.value = null;
    fetchDevices();
}
const clearFilters = () => {
    Object.keys(filters).forEach((key) => {
        ['data', 'action', 'latitude', 'longitude'].forEach(field => {
            if (filters[key] && filters[key].hasOwnProperty(field)) {
                filters[key][field] = null;
            }
        });
    });
    totalPages.value = 1;
    currentPage.value = 1;
    totalDevices.value = 0;
    sortedType.value = null;
    fetchDevices();
}
const subscriptionStatus = (device) => {
    if (!device.subscription_end_date) return null;
    const dateStr = device.subscription_end_date;
    const endDate = new Date(dateStr.includes('T') || dateStr.includes('+') || dateStr.endsWith('Z') ? dateStr : dateStr + 'T23:59:59Z');
    const now = new Date();
    const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return 'expired';
    if (daysLeft <= 30) return 'expiring';
    return 'active';
};
const expiredDeviceCount = computed(() => devices.value.filter(d => subscriptionStatus(d) === 'expired').length);
const expiringDeviceCount = computed(() => devices.value.filter(d => subscriptionStatus(d) === 'expiring').length);

const status = (status) => {
    switch (status) {
        case -1:
            return 'Non Reporting';
        case 0:
            return 'Active';
        case 1:
            return 'Disabled';
        case 2:
            return 'Faulty';
        case 3:
            return 'Lost';
    }
};
const signals = (strength) => {
    switch (strength) {
        case 0:
            return {
                icon: 'mdi-signal-off',
                color: '#dbdbdb'
            }
        case 1:
            return {
                icon: 'mdi-signal-cellular-outline',
                color: '#d70000'
            }
        case 2:
            return {
                icon: 'mdi-signal-cellular-1',
                color: '#ff9e01'
            }
        case 3:
            return {
                icon: 'mdi-signal-cellular-2',
                color: '#849e00'
            }
        case 4:
            return {
                icon: 'mdi-signal-cellular-3',
                color: '#099f00'
            }
        case 5:
            return {
                icon: 'mdi-signal-cellular-3',
                color: '#099f00'
            }
    }
}
const battery = (strength, type = null) => {
    return powerSourceIcon(strength, type)
}
const statusColor = (battery_bars, last_updated) => {
    let color = battery_bars ? battery(battery_bars)['color'] : '#d70000'
    color = getDifferenceInHours(last_updated) <= 30 ? color : '#d70000';
    switch (color) {
        case '#d70000':
            return {
                color: color,
                message: "Low Battery / Power Disconnected"
            }
        case '#ff9e01':
            return {
                color: color,
                message: "Device communicated rarely"
            }
        case '#849e00':
        case '#099f00':
            return {
                color: color,
                message: "Device communicated recently"
            }
    }
}
const getKnownLocation = (device) => {
    if (device.location) return device.location;
    // Skip cached values that are the old "Address not found" sentinel - older
    // builds persisted those on geocode failure, and we want to re-attempt
    // rather than display the bad string forever.
    if (isValidAddress(device.formatted_address)) return device.formatted_address;

    const key = device.id;

    if (geocodeCache.value[key]) return geocodeCache.value[key];

    if (!device.latitude || !device.longitude) {
        return '-';
    }
    // No address cached: show the raw coords while the async geocode runs.
    geocodeCache.value[key] = `${device.latitude}, ${device.longitude}`;

    geocodeLatLng({lat: device.latitude, lon: device.longitude})
        .then((response) => {
            if (!isValidAddress(response)) return;
            geocodeCache.value[key] = response;
            const index = devices.value.findIndex((item) => item.id === device.id);
            if (index !== -1) {
                devices.value[index]['formatted_address'] = response;
                updateGeocodedAddress(device.id, response);
            }
        })
        .catch(() => { /* leave the coord placeholder, retry on next load */ });

    return geocodeCache.value[key];
};
const deviceStats = ((device) => {
    const battery_bars = (device.battery1_status ? device.battery1_bars : (device.battery2_status ? device.battery2_bars : 0))
    const battery_type = device.battery1_status ? device.battery1_type : (device.battery2_status ? device.battery2_type : null)
    return {
        'battery_bars': battery_bars,
        'battery': battery(battery_bars, battery_type),
        'status_color': statusColor(battery_bars, device.last_updated),
    }
})
const applyQuickSearch = () => {
    if (quickSearch.value) {
        clearFilters();
    }
}
const applyCountFilter = (dc) => {
    if (dc.key === 'total') {
        clearFilters();
        return;
    }
    // Clear other filters first, then apply this one
    Object.keys(filters).forEach((key) => {
        ['data', 'action', 'latitude', 'longitude'].forEach(field => {
            if (filters[key] && filters[key].hasOwnProperty(field)) {
                filters[key][field] = null;
            }
        });
    });
    filters['device_fleet_status']['data'] = dc.key;
    currentPage.value = 1;
    fetchDevices();
}

const route = useRoute();
onMounted(() => {
    // Apply fleet status from URL query param if present
    if (route.query.device_fleet_status) {
        filters['device_fleet_status']['data'] = route.query.device_fleet_status;
    }
    if (user.type === 'admin') {
        fetchPortals();
        fetchCompaniesList();
        filters['device_group'] = {
            label: 'Device Group',
            type: 'select',
            data: null,
            title: 'name',
            value: 'id',
            multiple: true,
            source: device_groups
        }
        filters['portal'] = {
            label: 'Device Portal',
            type: 'select',
            data: null,
            title: 'name',
            value: 'id',
            source: portals
        }
    }
    fetchDevices();
    fetchProductSkusList();
});
onUnmounted(() => {
    const localStorageProfiles = JSON.parse(localStorage.getItem('filterProfiles'));
    if (localStorageProfiles?.selectedProfile) {
        localStorage.setItem('filterProfiles', JSON.stringify({
            selectedProfile: null,
            profiles: localStorageProfiles.profiles,
        }));
    }
})

const getDifferenceInHours = (timestamp) => {
    // Convert the timestamp string to a Date object
    const givenTime = new Date(timestamp);

    // Get the current time
    const currentTime = new Date();

    // Calculate the difference in milliseconds
    const differenceInMs = currentTime - givenTime;

    // Convert milliseconds to hours
    return (differenceInMs / (1000 * 60 * 60)).toFixed(0);
}
const copyAddress = (device) => {
    if (device.formatted_address
        && device.formatted_address !== ''
        && device.formatted_address !== 'Address not found') {
        copyToClipboard(device.formatted_address)
    } else {
        copyToClipboard(`${device.latitude}, ${device.longitude}`);
    }
}
const openStreetView = (device) => {
    if (device.formatted_address
        && device.formatted_address !== ''
        && device.formatted_address !== 'Address not found') {
        window.open(`http://maps.google.com/maps/dir//${device.formatted_address ?? ''}`, "_blank");
    } else {
        window.open(`http://maps.google.com/maps/dir//${device.latitude},${device.longitude}`, "_blank")
    }
};
const sort = (sorting) => {
    sortedColumn.value = sorting.column;
    sortedType.value = sorting.order;
    currentPage.value = 1
    fetchDevices();
}
const getPortal = (portalId) => {
    if (portals.value) {
        return portals.value.find((p) => p.id === portalId);
    }
    return undefined;
}
const getCompany = (companyId) => {
    if (companies.value) {
        return companies.value.find((p) => p.id === companyId);
    }
    return undefined;
}
const computeDeviceStats = () => {
    if (devices.value.length > 0) {
        devices.value.forEach(device => {
            device.stats = deviceStats(device);
        })
    }
}

const fetchCompaniesList = () => {
    send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Companies could not be loaded', 'error')
        })
}
const fetchProductSkusList = () => {
    send('get', `product-skus`, {
        list: true,
        portals: true,
        columns: JSON.stringify("id,sku")
    })
        .then((response) => {
            productSkus.value = response.data.data.product_skus;
        }).catch((error) => {
        showSnackbar('Product skus list could not be loaded', 'error')
    })
}
const fetchPortals = () => {
    send('get', `admin/portals`)
        .then((response) => {
            portals.value = response.data.data;
        }).catch((error) => {
        showSnackbar('Portals List could not be loaded', 'error')
    })
}
const fetchDevices = () => {
    localStorage.setItem('devicePerPage', perPage.value);
    let data = {
        page: currentPage.value,
        per_page: perPage.value,
        order: sortedType.value,
        sort: sortedColumn.value,
        quick_search: quickSearch.value,
    };
    if (showInactive.value) data.include_disabled = 1;
    let endpoint = `devices/list`;
    let method = 'get';
    if (Object.keys(activeFilters.value).length) {
        data = {...data, ...filters};
        endpoint = `devices/list-filters`;
        method = 'post';
    }

    loadingStore.showLoading('Loading...', 'Fetching your tracking devices');
    let loaded = false;
    send(method, endpoint, data)
        .then((response) => {
            loaded = true;
            devices.value = response.data.data.devices.data;
            totalDevices.value = response.data.data.devices.meta.total;
            totalPages.value = response.data.data.devices.meta.last_page;
            currentPage.value = response.data.data.devices.meta.current_page;
            device_counts.value = response.data.data.device_counts;
            device_groups.value = response.data.data.device_groups;
            computeDeviceStats();
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Devices could not be loaded', 'error')
        }).finally(async () => {
        loadingStore.hideLoading();
        if (user.type === 'company') {
            // An empty list only means "no devices" when the request actually
            // succeeded. This runs on the error path too, so a blip, a 500 or
            // the 401 that a sign out produces used to send an admin who has
            // devices into the setup wizard, with no way back to the list.
            if (!loaded || authStore.loggingOut) {
                return;
            }
            if (!devices.value.length && isAdmin.value) {
                navigateTo('setup-wizard');
            } else {
                if (!locations.value.length) {
                    await fetchCompanyLocations();
                }
                if (locations.value.length) {
                    checkDevicesLocation();
                }
            }
        }
    })
}

/**
 * Device Location Methods
 * */
const fetchCompanyLocations = async () => {
    if (user && user.type === 'company') {
        await send("get", `companies/locations/${user.company.id}`, {list: true})
            .then((response) => {
                locations.value = response.data.data;
            }).catch((errorResponse) => console.log(errorResponse));
    }
}
const updateGeocodedAddress = (device, address) => {
    send("put", `devices/update-address`, {device: device, address: address})
        .catch((errorResponse) => console.log(errorResponse));
}
const isPointInPolygon = (polygon, point) => {
    let inside = false;
    const x = parseFloat(point['longitude']);  // X = lon
    const y = parseFloat(point['latitude']);   // Y = lat

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = parseFloat(polygon[i]['longitude']);
        const yi = parseFloat(polygon[i]['latitude']);
        const xj = parseFloat(polygon[j]['longitude']);
        const yj = parseFloat(polygon[j]['latitude']);

        const intersect =
            ((yi > y) !== (yj > y)) &&
            (x < (xj - xi) * (y - yi) / (yj - yi) + xi);

        if (intersect) {
            inside = !inside;
        }
    }

    return inside;
}
const checkDevicesLocation = () => {
    setTimeout(function () {
        const devicesWithUnknownLocation = devices.value.filter((item, index) => {
            item.index = index;
            return item.location_updated && !item.location;
        });

        if (devicesWithUnknownLocation.length) {
            devicesWithUnknownLocation.map((item) => {
                locations.value.filter((location) => {
                    if (isPointInPolygon(location.polygon_coordinates, {
                        latitude: item.latitude,
                        longitude: item.longitude
                    })) {
                        setTimeout(updateDeviceLocation(item, location), 3000)
                    }
                })
                return item.location_id && !item.location;
            });
        }
    }, 5000)
}
const updateDeviceLocation = (device, location = null) => {
    if (device.index < 0) {
        return;
    }
    devices.value[device.index]['location'] = location.name;
    send("put", `devices/location`, {
        device: device.id,
        location: location.id
    }).catch((errorResponse) => console.log(errorResponse));
}

/**
 * Device Location Methods END
 * */


const createDevice = () => {
    selectedDevice.value = null;
    createDialogSwitch.value = true;
}
const createAssetDialogSwitch = ref(false);
const preSelectedDeviceForAsset = ref(null);
const isAdmin = computed(() => isCompanyAdminRole(authStore.user));
const checkingPaymentMethod = ref(false);
const paymentMethodRequired = ref(false);
const paymentMethodAction = ref('');

const hasPaymentMethod = async () => {
    try {
        const response = await send('get', 'payments/methods');
        if (response?.data?.success) {
            const data = response.data.data;
            const methods = Array.isArray(data) ? data : (data?.payment_methods || []);
            return methods.length > 0;
        }
        return true; // fail-open
    } catch {
        return true; // fail-open
    }
};

const checkPaymentMethodBeforeAction = async (actionLabel) => {
    checkingPaymentMethod.value = true;
    const has = await hasPaymentMethod();
    checkingPaymentMethod.value = false;
    if (!has) {
        paymentMethodAction.value = actionLabel;
        paymentMethodRequired.value = true;
        return false;
    }
    return true;
};

const onActivateDevice = () => {
    navigateTo('setup-wizard');
};

const addDevice = (device) => {
    selectedDevice.value = null;
    // devices.value.unshift(device);
    currentPage.value = 1;
    fetchDevices();
}
const assignToAsset = async (device) => {
    const canProceed = await checkPaymentMethodBeforeAction('add an asset');
    if (!canProceed) return;
    preSelectedDeviceForAsset.value = device;
    createAssetDialogSwitch.value = true;
}
const onAssetCreated = () => {
    createAssetDialogSwitch.value = false;
    preSelectedDeviceForAsset.value = null;
    fetchDevices();
}
const copyDevice = (device) => {
    selectedDevice.value = device;
    createDialogSwitch.value = true;
}
const editDevice = (device) => {
    selectedDevice.value = device;
    editDialogSwitch.value = true;
}
const updateDevice = () => {
    selectedDevice.value = null;
    fetchDevices();
}
const getSimStatus = (device) => {
    selectedDevice.value = device;
    simStatusDialogSwitch.value = true;
}
const getOtaStatus = (device) => {
    selectedDevice.value = device;
    otaStatusDialogSwitch.value = true;
}
const paymentExclusion = (device) => {
    selectedDevice.value = device;
    paymentExclusionDialogSwitch.value = true;
}
const sendMessage = (device) => {
    selectedDevice.value = device;
    messageDialogSwitch.value = true;
}
const configFile = (device) => {
    selectedDevice.value = device;
    configFileDialogSwitch.value = true;
}
const openMessageLogs = (device) => {
    // Always device_id: the Message Log filter is the device picker now,
    // so linking by asset_id would apply a filter with no visible control
    // and no way to clear it.
    const query = {device_id: device?.id};
    router.push({name: 'messageLog', query});
}
const repairLicense = async (device) => {
    if (!confirm(`Queue license repair for device ${device.slno}?\n\nThis will write a new license key and reboot the device.`)) return;
    try {
        const response = await send('post', `devices/${device.id}/repair-license`);
        if (response?.data?.success) {
            alert(`License repair queued for ${device.slno}.\nLicense: ${response.data.data.license}\nDevice will reboot after receiving the command.`);
        } else {
            alert(`Failed: ${response?.data?.message || 'Unknown error'}`);
        }
    } catch (e) {
        alert(`Error: ${e.response?.data?.message || e.message}`);
    }
}
const toggleSaveRawPackets = async (device) => {
    const newStatus = device.save_raw_packets ? 0 : 1;
    try {
        const response = await send('post', `devices/${device.id}/toggle-save-raw-packets`, {status: newStatus});
        if (response?.data?.success) {
            device.save_raw_packets = response.data.data.save_raw_packets;
            showSnackbar(response.data.message);
        }
    } catch (e) {
        showSnackbar(e.response?.data?.message || 'Failed to toggle raw packet logging', 'error');
    }
}

// Force delete. The v-if on the menu item is cosmetic - the endpoint re-checks
// for super admin and that check is the real boundary.
const forceDeleteDialog = ref(false);
const forceDeleteDevice = ref(null);
const forceDeletePreview = ref(null);
const forceDeleteLoading = ref(false);
const forceDeleteBusy = ref(false);
const forceDeleteStep = ref(1);
const forceDeleteConfirm = ref('');
const forceDeleteError = ref('');

const forceDeleteBlocked = computed(() => (forceDeletePreview.value?.blockers?.length ?? 0) > 0);

// notThisDevice counts rows swept in through the asset rather than the device
// itself, which is normal for an asset's own history but is also how a
// misattached asset would show up. Surfaced per table rather than buried in
// the total.
const forceDeleteRows = computed(() => {
    const foreign = forceDeletePreview.value?.not_this_device || {};
    return Object.entries(forceDeletePreview.value?.counts || {})
        .filter(([, count]) => count > 0)
        .map(([table, count]) => ({table, count, notThisDevice: foreign[table] || 0}))
        .sort((a, b) => b.count - a.count);
});

const forceDeleteForeignTotal = computed(
    () => Object.values(forceDeletePreview.value?.not_this_device || {}).reduce((sum, n) => sum + n, 0)
);

const forceDeletePreserved = computed(() => Object.entries(forceDeletePreview.value?.preserved || {})
    .filter(([, count]) => count > 0)
    .map(([table, count]) => ({table, count})));

// Billing lines are destroyed rather than detached, unlike everything else
// under "kept", so they get called out separately instead of being one row in
// a table of forty.
const forceDeleteInvoiceCount = computed(() => forceDeletePreview.value?.counts?.invoice_item_details || 0);

// slno is a char(16) and comes back space padded, so both sides are trimmed.
const forceDeleteSerialMatches = computed(() => {
    const typed = forceDeleteConfirm.value.trim();
    return typed !== '' && typed === (forceDeleteDevice.value?.slno || '').trim();
});

const formatBytes = (bytes) => {
    let value = Number(bytes) || 0;
    if (value < 1024) return `${value} B`;
    const units = ['KB', 'MB', 'GB', 'TB'];
    let unit = -1;
    do {
        value /= 1024;
        unit++;
    } while (value >= 1024 && unit < units.length - 1);
    return `${value.toFixed(1)} ${units[unit]}`;
}

const openForceDelete = async (device) => {
    forceDeleteDevice.value = device;
    forceDeletePreview.value = null;
    forceDeleteStep.value = 1;
    forceDeleteConfirm.value = '';
    forceDeleteError.value = '';
    forceDeleteBusy.value = false;
    forceDeleteLoading.value = true;
    forceDeleteDialog.value = true;
    try {
        const response = await send('get', `admin/devices/${device.id}/force-delete/preview`);
        forceDeletePreview.value = response.data.data;
    } catch (e) {
        forceDeleteError.value = e.response?.data?.message || 'Could not load the force delete preview.';
    } finally {
        forceDeleteLoading.value = false;
    }
}

const runForceDelete = async () => {
    const device = forceDeleteDevice.value;
    forceDeleteBusy.value = true;
    forceDeleteError.value = '';
    const serial = (device.slno || '').trim();

    let response;
    try {
        response = await send('post', `admin/devices/${device.id}/force-delete`, {
            confirm_slno: forceDeleteConfirm.value.trim(),
        });
    } catch (e) {
        // Left open on purpose so the operator can read what went wrong.
        forceDeleteError.value = e.response?.data?.message || 'Force delete failed.';
        showSnackbar(forceDeleteError.value, 'error');
        forceDeleteBusy.value = false;
        return;
    }

    // Past this point the rows are already gone, so nothing that fails here is
    // a failed delete and none of it may be reported as one. The dump in hand
    // is the only copy of what was destroyed, so if it cannot be written out,
    // say exactly that and leave it somewhere the operator can still reach.
    try {
        downloadJson(response.data.data, `device-${serial}-deleted-${fileTimestamp()}.json`);
    } catch (e) {
        console.error('Force delete recovery dump for device', serial, response.data.data);
        forceDeleteError.value = 'The device was deleted, but the recovery file could not be saved. '
            + 'Do not close this tab: the dump has been written to the browser console.';
        showSnackbar(forceDeleteError.value, 'error');
        forceDeleteBusy.value = false;
        return;
    }

    forceDeleteDialog.value = false;
    const savedTo = response.data.data?.meta?.saved_to;
    showSnackbar(savedTo
        ? `${response.data.message} Also saved on the server at ${savedTo}.`
        : response.data.message, 'success', 8000);
    fetchDevices();
    forceDeleteBusy.value = false;
}

// ISO 8601 basic format. The extended form's colons are illegal in Windows
// filenames and browsers silently rewrite them on download.
const fileTimestamp = () => new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');

const downloadJson = (payload, fileName) => {
    const url = window.URL.createObjectURL(new Blob([JSON.stringify(payload, null, 2)], {type: 'application/json'}));
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Deferred: revoking in the same tick can cancel the download in Firefox.
    setTimeout(() => window.URL.revokeObjectURL(url), 0);
}

const editUpdateDeviceGroup = (device) => {
    selectedDevice.value = device;
    deviceGroupDialogSwitch.value = true;
}
const deviceGroupDetach = (device, device_group) => {
    if (confirm(`Are you sure to detach "${device_group.name}" from "${device.slno}" device?`)) {
        send('delete', `device/${device.id}/device-groups/${device_group.id}`)
            .then((response) => {
                if (response.data.success) {
                    devices.value.map((dev, index) => {
                        if (dev.id === device.id) {
                            dev.device_groups.map((group, groupIndex) => {
                                if (group.id === device_group.id) {
                                    dev.device_groups.splice(groupIndex, 1);
                                }
                            });
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                console.log(error)
                showSnackbar(`"${device_group.name}" could not be detached from "${device.slno}" device`, 'error')
            })
    }
}
const deleteDevice = (device) => {
    if (confirm(`Are you sure to delete "${device.slno}" device?`)) {
        send('delete', `devices/${device.id}`)
            .then((response) => {
                if (response.data.success) {
                    devices.value.map((dev, index) => {
                        if (dev.id === device.id) {
                            devices.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                console.log(error)
                showSnackbar(`"${device.slno}" Device could not be deleted`, 'error')
            })
    }
}
// Update device groups working
const updateGroupDevices = (device) => {
    devices.value.map((comp, index) => {
        if (comp.id === device.id) {
            devices.value[index] = device;
        }
    });
}
const endSubscription = (device) => {
    if (!confirm(`Are you sure to end subscription for device: ${device.slno} ?`)) {
        return false;
    }

    send('post', `owner/devices/${device.slno}/end-subscription`)
        .then((response) => {
            if (response.data.success) {
                devices.value.map((dev, index) => {
                    if (dev.id === device.id) {
                        devices.value.splice(index, 1);
                    }
                });
                showSnackbar(response.data.message);
            }
        })
        .catch((error) => {
            console.log(error)
            showSnackbar(`"${device.slno}" Device could not be disassociated`, 'error')
        })
}
const downloadConfigFile = (device) => {
    loadingStore.showLoading('Downloading', 'Fetching Device Config Details...');
    send('get', `admin/device-configs/${device.id}/device`, {download_link: 1})
        .then(async (response) => {
            if (response.data.data) {
                const downloadLink = response.data.data;
                if (downloadLink) {
                    loadingStore.updateLoadingMessage('Downloading', 'Requesting Download...');
                    await send('post', downloadLink)
                        .then((response) => {
                            loadingStore.updateLoadingMessage('Downloading', 'Preparing File...');
                            handleDownloadResponse(response, `Config-File-${device.slno}`);
                        }).catch((error) => {
                            showSnackbar('Config file could not be downloaded', 'error')
                        })
                }
            } else {
                showSnackbar("Config file doesn't exist", 'error')
            }
        }).catch((error) => {
        showSnackbar('Config file could not be downloaded', 'error')
    }).finally(() => loadingStore.hideLoading());
}
const handleDownloadResponse = (response, fileName) => {
    if (!response.data) {
        console.error("Server returned no data.");
        return;
    }

    const blob = new Blob([response.data], {type: 'text/plain'});
    if (window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveBlob(blob, fileName);
    } else {
        const elem = window.document.createElement('a');
        elem.href = window.URL.createObjectURL(blob);
        elem.download = fileName;
        document.body.appendChild(elem);
        elem.click();
        document.body.removeChild(elem);
    }
}
</script>
